import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import AddWantForm from '../components/AddWantForm';

const OnboardingStep6 = () => {
  const navigate = useNavigate();
  const [wants, setWants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load user's wants when component mounts
  useEffect(() => {
    const loadWants = async () => {
      const user = auth.currentUser;
      if (!user) {
        console.log('⏳ Waiting for user authentication...');
        return;
      }

      try {
        console.log('🔍 OnboardingStep6: Loading wants for user:', user.uid);
        const wantsQuery = query(
          collection(db, 'wantList'),
          where('ownerID', '==', user.uid)
        );
        const wantsSnapshot = await getDocs(wantsQuery);
        console.log('📊 OnboardingStep6: Found', wantsSnapshot.docs.length, 'wants');
        const wantsData = wantsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('✅ OnboardingStep6: Wants data:', wantsData);
        setWants(wantsData);
        setLoading(false);
      } catch (error) {
        console.error('❌ OnboardingStep6: Error loading wants:', error);
        setLoading(false);
      }
    };

    // Add a small delay to ensure auth is ready, or listen to auth state
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        loadWants();
      }
    });

    return () => unsubscribe();
  }, []);

  // Refresh wants list after adding a new want
  const handleWantAdded = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const wantsQuery = query(
      collection(db, 'wantList'),
      where('ownerID', '==', user.uid)
    );
    const wantsSnapshot = await getDocs(wantsQuery);
    const wantsData = wantsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setWants(wantsData);
    
    // Scroll to top to show the newly added want in the Dream Pedal slots
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContinue = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error('No authenticated user');
        return;
      }

      // Mark step 6 as complete
      await updateDoc(doc(db, 'users', user.uid), {
        step6Complete: true
      });

      // Navigate to Step 7
      navigate('/onboarding-step-7');
    } catch (error) {
      console.error('Error completing step 6:', error);
    }
  };

  const handleSkip = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error('No authenticated user');
        return;
      }

      // Mark step 6 as complete (even though skipped)
      await updateDoc(doc(db, 'users', user.uid), {
        step6Complete: true
      });

      // Navigate to Step 7
      navigate('/onboarding-step-7');
    } catch (error) {
      console.error('Error skipping step 6:', error);
    }
  };

  return (
    <>
      <style>{`
        @media (max-width: 1024px) {
          body {
            padding-bottom: 0 !important;
          }
          .onboarding-container {
            min-height: calc(100vh - var(--navbar-height, 125px)) !important;
            padding-bottom: 120px !important;
          }
        }
      `}</style>
      <div className="onboarding-container" style={{ 
        minHeight: '100vh', 
        backgroundColor: '#f5f5f5', 
        padding: '2rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
      <div style={{ 
        maxWidth: '800px', 
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '3rem 2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        boxSizing: 'border-box'
      }}
      className="onboarding-step6-container">
        {/* Header */}
        <h1 style={{
          fontFamily: 'Nunito, sans-serif',
          fontSize: '1.875rem',
          fontWeight: '700',
          color: '#4A5759',
          textAlign: 'center',
          marginBottom: '0.5rem',
          lineHeight: '1.4'
        }}>
          What are you looking for? <span style={{ color: '#C9A227' }}>(Optional)</span>
        </h1>
        <p style={{
          fontFamily: 'Nunito, sans-serif',
          fontSize: '1.125rem',
          color: '#6b7280',
          textAlign: 'center',
          marginBottom: '0.25rem',
          lineHeight: '1.4'
        }}>
          Add at least one pedal you want
        </p>
        <p style={{
          fontFamily: 'Nunito, sans-serif',
          fontSize: '1rem',
          color: '#6b7280',
          textAlign: 'center',
          marginBottom: '2rem',
          lineHeight: '1.4'
        }}>
          (We use this to find your first matches)
        </p>

        {/* Wantlist Slots (Show first 3 wants) */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem',
            marginBottom: '2rem'
          }}
          className="dream-pedal-slots">
            {[0, 1, 2].map((index) => {
              const want = wants[index];
              return (
                <div
                  key={index}
                  style={{
                    border: want ? '2px solid #C9A227' : '2px dashed #d1d5db',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    minHeight: '60px',
                    backgroundColor: want ? '#fffbf0' : '#f9fafb',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {want ? (
                    <>
                      <div style={{
                        fontFamily: 'Nunito, sans-serif',
                        fontSize: '0.9rem',
                        fontWeight: '700',
                        color: '#4A5759',
                        marginBottom: '0.25rem'
                      }}>
                        {want.make}
                      </div>
                      <div style={{
                        fontFamily: 'Nunito, sans-serif',
                        fontSize: '0.85rem',
                        color: '#6b7280',
                        marginBottom: '0.25rem'
                      }}>
                        {want.model}
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        fontSize: '0.75rem',
                        color: '#9ca3af',
                        fontFamily: 'Nunito, sans-serif',
                        justifyContent: 'center'
                      }}>
                        <span>${want.maxPrice}</span>
                        <span>•</span>
                        <span>{want.condition}</span>
                      </div>
                    </>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.875rem'
                    }}>
                      Wanted Pedal {index + 1}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Want Counter */}
          <div style={{
            textAlign: 'center',
            fontFamily: 'Nunito, sans-serif',
            fontSize: '0.875rem',
            color: wants.length > 0 ? '#16a34a' : '#6b7280',
            fontWeight: '600',
            marginBottom: '1rem'
          }}>
            {wants.length} of 3 want{wants.length !== 1 ? 's' : ''} added
          </div>
        </div>

        {/* Add Want Form */}
        <div style={{
          borderTop: '2px solid #e5e7eb',
          paddingTop: '2rem',
          marginBottom: '2rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          {wants.length < 3 ? (
            <AddWantForm onWantAdded={handleWantAdded} hideNotes />
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              border: '2px solid #86efac'
            }}>
              <p style={{
                fontFamily: 'Nunito, sans-serif',
                fontSize: '1rem',
                color: '#16a34a',
                fontWeight: '600'
              }}>
                ✓ You've added the maximum of 3 wants! Ready to continue?
              </p>
            </div>
          )}
        </div>

        {/* Skip Link */}
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <button
            onClick={handleSkip}
            style={{
              background: 'none',
              border: 'none',
              color: '#5A9ECB',
              textDecoration: 'underline',
              fontSize: '1.125rem',
              fontFamily: 'Nunito, sans-serif',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            Skip This Step
          </button>
        </div>

        {/* Continue Button */}
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            onClick={handleContinue}
            style={{
              backgroundColor: '#C9A227',
              color: 'white',
              padding: '0.875rem 3rem',
              border: 'none',
              borderRadius: '9999px',
              fontSize: '1.125rem',
              fontWeight: 'bold',
              fontFamily: 'Nunito, sans-serif',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(201, 162, 39, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#b8912a';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(201, 162, 39, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#C9A227';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(201, 162, 39, 0.3)';
            }}
          >
            Continue
          </button>
        </div>

        {/* Add responsive CSS for mobile */}
        <style>
          {`
            @media (max-width: 768px) {
              .onboarding-step6-container {
                padding: 2rem 0.75rem !important;
              }
              .dream-pedal-slots {
                grid-template-columns: 1fr !important;
                padding-left: 0 !important;
                padding-right: 0 !important;
                margin-left: 0 !important;
                margin-right: 0 !important;
              }
              .missing-pedal-notice {
                margin-left: -0.75rem !important;
                margin-right: -0.75rem !important;
                max-width: none !important;
                width: calc(100% + 1.5rem) !important;
              }
              body {
                overflow-x: hidden;
              }
            }
          `}
        </style>
      </div>
    </div>
    </>
  );
};

export default OnboardingStep6;

